import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Item } from '../types';
import { CONFIG } from '../config-global';

type PurchaseData = {
  date: string;
  customerName: string;
  customerAddress?: string;
  customerCCCD?: string;
  items: Item[];
  totalAmount: number;
};

interface PurchaseReportProps {
  date: Date;
  purchases: PurchaseData[];
  mode?: 'separate' | 'combined';
}

export const PurchaseReport: React.FC<PurchaseReportProps> = ({
  purchases,
  date,
  mode = 'separate',
}) => {

  const cell_style = { border: '1px solid black', fontSize: 12, padding: '4px' };
  const header_style = { ...cell_style, fontWeight: 'bold', backgroundColor: '#f5f5f5' };
  const totalAmount = purchases.reduce((sum, p) => sum + p.totalAmount, 0);

  const reportDate = "Ngày " + date.getDate().toString().padStart(2, '0') +
    " tháng " + (date.getMonth() + 1).toString().padStart(2, '0') +
    " năm " + date.getFullYear();

  const reportHeader = (
    <>
      {/* Header */}
      <Box sx={{ textAlign: 'center', width: 'fit-content', border: '1px solid black', p: 1 }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: 10 }}>
          <strong>Mẫu số: 01/TNĐN</strong>
        </Typography>
        <Typography sx={{ fontSize: 9 }}>
          (Ban hành kèm theo Thông tư số<br/> 78/2014/TT-BTC của Bộ Tài chính)
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'center', mb: 3, mt: -5 }}>
        <Typography sx={{ fontWeight: 'bold', mb: 1, width: '100%', fontSize: 14 }}>
          BẢNG KÊ THU MUA HÀNG HÓA, DỊCH VỤ<br />
          MUA VÀO KHÔNG CÓ HÓA ĐƠN
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 11 }}>
          ({reportDate})
        </Typography>
      </Box>

      {/* Info Section */}
      <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: '4fr 1fr', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 12 }}>
            - Tên doanh nghiệp: <strong>{CONFIG.businessName}</strong>
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 1 }}>
            - Địa chỉ: <strong>{CONFIG.businessAddress}</strong>
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 1 }}>
            - Địa chỉ nơi tổ chức thu mua: <strong>{CONFIG.businessAddress}</strong>
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 1 }}>
            - Người phụ trách thu mua: .................................................................
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <Box sx={{ border: '1px solid black', p: 1, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              Mã số thuế:
            </Typography>
            <Typography sx={{ fontWeight: 'bold', mt: 1 }}>
              {CONFIG.taxCode}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  )

  const reportFooter = (
    <Box sx={{ mt: 4, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ height: 25 }}></Typography>
        <Typography sx={{ fontWeight: 'bold' }}>
          Người lập bảng kê
        </Typography>
        <Typography sx={{ fontSize: 12 }}>(Ký, ghi rõ họ tên)</Typography>
        <Box sx={{ height: 60 }} />
        ..................................................
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ height: 25 }}>
          {reportDate}
        </Typography>
        <Typography sx={{ fontWeight: 'bold' }}>
          Giám đốc doanh nghiệp
        </Typography>
        <Typography sx={{ fontSize: 12 }} >(Ký tên, đóng dấu)</Typography>
        <Box sx={{ height: 60 }} />
        ..................................................
      </Box>
    </Box>
  );

  const tableHeader = (
    <TableHead>
      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
        <TableCell align="center" rowSpan={2} sx={header_style} width={100}>
          Ngày tháng năm mua hàng
        </TableCell>
        <TableCell align="center" colSpan={3} sx={header_style}>
          Người bán
        </TableCell>
        <TableCell align="center" colSpan={4} sx={header_style}>
          Hàng hóa mua vào
        </TableCell>
        <TableCell align="center" rowSpan={2} sx={header_style}>
          Ghi chú
        </TableCell>
      </TableRow>
      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
        <TableCell align="center" sx={header_style}>
          Tên người bán
        </TableCell>
        <TableCell align="center" sx={{ ...header_style }}>
          Địa chỉ
        </TableCell>
        <TableCell align="center" sx={header_style}>
          CCCD
        </TableCell>
        <TableCell align="center" sx={header_style}>
          Tên mặt hàng
        </TableCell>
        <TableCell align="center" sx={header_style}>
          Số lượng
        </TableCell>
        <TableCell align="center" sx={header_style}>
          Đơn giá
        </TableCell>
        <TableCell align="center" sx={header_style}>
          Thành tiền
        </TableCell>
      </TableRow>
    </TableHead>
  );

  // Render combined report (all customers in one page)
  if (mode === 'combined') {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          fontSize: 12,
          mx: 'auto',
          bgcolor: 'white',
          '@media print': {
            boxShadow: 'none',
            p: 4,
            pageBreakAfter: 'avoid',
          },
        }}
      >
        {reportHeader}
        {/* Table */}
        <TableContainer sx={{ mb: 3 }}>
          <Table border={1} sx={{ border: '1px solid black' }}>
            {tableHeader}
            <TableBody>
              {purchases.sort((a, b) => a.date > b.date ? 1 : -1).map((purchase, purchaseIndex) =>
                purchase.items.sort((a, b) => a.fruitTypeId > b.fruitTypeId ? 1 : -1).map((item, itemIndex) => (
                  <TableRow key={`${purchaseIndex}-${itemIndex}`}>
                    {itemIndex === 0 ? (
                      <>
                        <TableCell align="center" sx={cell_style}>
                          {new Date(purchase.date).toLocaleDateString('vi-VN')}
                        </TableCell>
                        <TableCell sx={cell_style}>
                          {purchase.customerName}
                        </TableCell>
                        <TableCell sx={cell_style}>
                          {purchase.customerAddress || ''}
                        </TableCell>
                        <TableCell sx={cell_style}>
                          {purchase.customerCCCD || ''}
                        </TableCell>
                      </>
                    ) :
                      <>
                        <TableCell sx={cell_style} />
                        <TableCell sx={cell_style} />
                        <TableCell sx={cell_style} />
                        <TableCell sx={cell_style} />
                      </>
                    }

                    <TableCell sx={cell_style}>
                      {item.fruitTypeName}
                    </TableCell>
                    <TableCell align="center" sx={cell_style}>
                      {item.weight}
                    </TableCell>
                    <TableCell align="right" sx={cell_style}>
                      {item.price.toLocaleString('vi-VN')}
                    </TableCell>
                    <TableCell align="right" sx={cell_style}>
                      {item.total.toLocaleString('vi-VN')}
                    </TableCell>
                    <TableCell sx={cell_style} />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography>
          - Tổng giá trị hàng hóa mua vào: <strong>{totalAmount.toLocaleString('vi-VN')} đ</strong>
        </Typography>

        {reportFooter}
      </Paper>
    );
  }

  // Render separate report (each customer on separate page)
  return (
    <>
      {purchases.sort((a, b) => a.date > b.date ? -1 : 1).map((purchase, purchaseIndex) => (
        <Paper
          key={purchaseIndex}
          elevation={3}
          sx={{
            p: 4,
            fontSize: 12,
            mx: 'auto',
            bgcolor: 'white',
            mb: 4,
            '@media print': {
              boxShadow: 'none',
              p: 0,
              mb: 0,
              pageBreakAfter: 'always',
            },
          }}
        >
          {reportHeader}

          {/* Table */}
          <TableContainer sx={{ mb: 3 }}>
            <Table border={1} sx={{ border: '1px solid black' }}>
              {tableHeader}
              <TableBody>
                {purchase.items.sort((a, b) => a.fruitTypeId > b.fruitTypeId ? 1 : -1).map((item, itemIndex) => (
                  <TableRow key={itemIndex}>
                    {/* Người bán info - chỉ row đầu */}
                    {itemIndex === 0 ? (
                      <>
                        <TableCell align="center" sx={cell_style}>
                          {new Date(purchase.date).toLocaleDateString('vi-VN')}
                        </TableCell>
                        <TableCell sx={cell_style}>
                          {purchase.customerName}
                        </TableCell>
                        <TableCell sx={cell_style}>
                          {purchase.customerAddress || ''}
                        </TableCell>
                        <TableCell sx={cell_style}>
                          {purchase.customerCCCD || ''}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={cell_style} />
                        <TableCell sx={cell_style} />
                        <TableCell sx={cell_style} />
                        <TableCell sx={cell_style} />
                      </>
                    )}

                    {/* Hàng hóa info */}
                    <TableCell sx={cell_style}>
                      {item.fruitTypeName}
                    </TableCell>
                    <TableCell align="center" sx={cell_style}>
                      {item.weight}
                    </TableCell>
                    <TableCell align="right" sx={cell_style}>
                      {item.price.toLocaleString('vi-VN')}
                    </TableCell>
                    <TableCell align="right" sx={cell_style}>
                      {item.total.toLocaleString('vi-VN')}
                    </TableCell>
                    <TableCell sx={cell_style} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={{ fontSize: 12 }}>
            - Tổng giá trị hàng hóa mua vào: <strong>{purchase.totalAmount.toLocaleString('vi-VN')} đ</strong>
          </Typography>

          {reportFooter}
        </Paper>
      ))}
    </>
  );
};
